import { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import {
  Squares2X2Icon,
  Cog6ToothIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  CircleStackIcon,
  CodeBracketIcon,
  LinkIcon,
  CheckIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  ShareIcon,
  TicketIcon,
} from '@heroicons/react/24/outline'
import type { Connection } from '@stride/shared'
import { useAuth } from '../../hooks/useAuth'
import { useAppContext } from '../../context/AppContext'
import { useConnectionService } from '../../services/connections/ConnectionServiceContext'
import { parseRepoUrl } from '../../lib/repoUrl'
import { ProjectSwitcher } from './ProjectSwitcher'

// ── Nav item definitions ──────────────────────────────────────────────────────

interface NavItem {
  label: string
  icon:  React.ComponentType<React.SVGProps<SVGSVGElement>>
  to:    string
}

interface NavGroup {
  group: string
  items: NavItem[]
}

// Consolidated IA: 13 flat items → 7, grouped (Workspace / Monitor / Admin).
//   Compare      → a mode inside Retrieve & Deploy (P4)
//   Branches·Commits·Pull Requests·Releases → Repository (tabs)
//   Deployments Matrix  → Pipeline ·  Deployments History → Activity
//   Initialize Global Key → Settings → Setup tools
const NAV_GROUPS: NavGroup[] = [
  {
    group: 'Workspace',
    items: [
      { label: 'Dashboard',         icon: Squares2X2Icon,   to: '/dashboard' },
      { label: 'Pipeline',          icon: ShareIcon,        to: '/pipeline' },
      { label: 'Delivery',          icon: TicketIcon,       to: '/delivery' },
      { label: 'Retrieve & Deploy', icon: ArrowDownTrayIcon, to: '/retrieve-deploy' },
    ],
  },
  {
    group: 'Monitor',
    items: [
      { label: 'Activity',   icon: ChartBarIcon,    to: '/activity' },
      { label: 'Repository', icon: CodeBracketIcon, to: '/repository' },
    ],
  },
  {
    group: 'Admin',
    items: [
      { label: 'Connections', icon: LinkIcon,      to: '/connections' },
      { label: 'Settings',    icon: Cog6ToothIcon, to: '/settings' },
    ],
  },
]

// ── Props ─────────────────────────────────────────────────────────────────────

interface SidebarProps {
  collapsed: boolean
  onToggle:  () => void
}

// ── Nav link helper ───────────────────────────────────────────────────────────

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }): JSX.Element {
  const Icon = item.icon
  return (
    <NavLink
      to={item.to}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        clsx(
          'relative flex items-center gap-3 rounded-control px-2.5 py-2 text-sm transition-colors',
          collapsed ? 'justify-center' : '',
          isActive
            // active: indigo-soft fill + indigo text + a left accent bar
            ? 'bg-accent-soft font-semibold text-accent-600 before:absolute before:-left-2 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r before:bg-accent before:content-[""]'
            : 'font-medium text-fg-muted hover:bg-surface-2 hover:text-fg',
        )
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  )
}

// ── Repo switcher ─────────────────────────────────────────────────────────────
//
// Lists git connections and lets the operator pick the active one (the single
// source of truth that drives every git-scoped surface).  Reads connections via
// a plain effect + state so the control works even where its tests omit a
// QueryClientProvider.  With zero git connections it routes to /connections.

/** Best display label for a git connection — its label, else the parsed repo. */
function gitConnectionLabel(conn: Connection): string {
  if (conn.label && conn.label.trim() !== '') return conn.label
  const ref = parseRepoUrl(conn.repoUrl)
  return ref ? `${ref.owner}/${ref.repo}` : 'Git repository'
}

function RepoSwitcher({ collapsed }: { collapsed: boolean }): JSX.Element {
  const navigate = useNavigate()
  const connSvc  = useConnectionService()
  const { activeConnectionId, setActiveConnectionId, connectionsVersion } = useAppContext()

  const [gitConnections, setGitConnections] = useState<Connection[]>([])
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    connSvc.listConnections()
      .then((conns) => {
        if (!cancelled) setGitConnections(conns.filter((c) => c.kind === 'git'))
      })
      .catch(() => {
        if (!cancelled) setGitConnections([])
      })
    return () => { cancelled = true }
  // `connectionsVersion` is the whole fix. The shell never unmounts, so with `[connSvc]` alone this
  // effect ran once at startup and the switcher kept saying "Connect a repo" after a repo had been
  // connected — until the app was restarted, which is exactly what Kunal had to do.
  }, [connSvc, connectionsVersion])

  // Close the menu on outside click.
  useEffect(() => {
    if (!open) return
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  const active = gitConnections.find((c) => c.id === activeConnectionId) ?? null
  const hasConnections = gitConnections.length > 0

  const label = active
    ? gitConnectionLabel(active)
    : hasConnections
      ? 'Select repo'
      : 'Connect a repo'

  function handleTriggerClick() {
    if (!hasConnections) {
      navigate('/connections')
      return
    }
    setOpen((o) => !o)
  }

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={handleTriggerClick}
        aria-haspopup={hasConnections ? 'menu' : undefined}
        aria-expanded={hasConnections ? open : undefined}
        className={clsx(
          'w-full flex items-center gap-2 px-2 py-2 rounded-md text-sm',
          'text-fg-muted hover:bg-surface-2 hover:text-fg transition-colors',
          collapsed ? 'justify-center' : '',
        )}
        title={collapsed ? label : undefined}
        data-testid="repo-switcher"
      >
        <CircleStackIcon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {!collapsed && <span className="truncate flex-1 text-left">{label}</span>}
        {!collapsed && hasConnections && (
          <ChevronDownIcon className="h-4 w-4 shrink-0 text-fg-subtle" />
        )}
      </button>

      {open && hasConnections && (
        <div
          role="menu"
          data-testid="repo-switcher-menu"
          className="absolute z-20 mt-1 left-0 right-0 min-w-[12rem] bg-surface-2 border border-border rounded-md shadow-lg py-1"
        >
          {gitConnections.map((c) => {
            const isActive = c.id === activeConnectionId
            return (
              <button
                key={c.id}
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => {
                  setActiveConnectionId(c.id)
                  setOpen(false)
                }}
                className={clsx(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors',
                  isActive ? 'text-fg bg-surface-3/60' : 'text-fg-muted hover:bg-surface-3/40',
                )}
              >
                <span className="truncate flex-1">{gitConnectionLabel(c)}</span>
                {isActive && <CheckIcon className="h-4 w-4 shrink-0 text-accent" />}
              </button>
            )
          })}
          <div className="border-t border-border my-1" />
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false)
              navigate('/connections')
            }}
            className="w-full flex items-center gap-2 px-3 py-2 text-xs text-fg-muted hover:bg-surface-3/40 hover:text-fg transition-colors"
          >
            <LinkIcon className="h-3.5 w-3.5 shrink-0" />
            Manage connections
          </button>
        </div>
      )}
    </div>
  )
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

export function Sidebar({ collapsed, onToggle }: SidebarProps): JSX.Element {
  const { account, logout } = useAuth()

  const displayName = account?.name ?? account?.username ?? 'User'
  const initial = displayName.charAt(0).toUpperCase()

  return (
    <aside
      data-testid="sidebar"
      className={clsx(
        'flex flex-col bg-surface shrink-0 transition-all duration-200 overflow-hidden',
        collapsed ? 'w-14' : 'w-56',
      )}
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-3 py-4 border-b border-border">
        {!collapsed && (
          <span className="text-fg font-semibold text-sm">Stride</span>
        )}
        {collapsed && (
          <span className="text-fg font-semibold text-sm mx-auto">SC</span>
        )}
        <button
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className="text-fg-muted hover:text-fg transition-colors ml-auto"
        >
          {collapsed
            ? <ChevronRightIcon className="h-4 w-4" />
            : <ChevronLeftIcon  className="h-4 w-4" />}
        </button>
      </div>

      {/* ── Repo switcher ───────────────────────────────────────────────────── */}
      <div className="px-2 py-3 border-b border-border">
        {/* Project first: a project CONTAINS its connections, and the reverse order implies a repo
            can span projects — exactly what the one-repo-per-project rule forbids. */}
        <ProjectSwitcher collapsed={collapsed} />
        <RepoSwitcher collapsed={collapsed} />
      </div>

      {/* ── Navigation (grouped: Workspace / Monitor / Admin) ──────────────── */}
      <nav className="flex-1 overflow-y-auto px-3 py-3" aria-label="Main navigation">
        {NAV_GROUPS.map((grp) => (
          <div key={grp.group} className="mb-1">
            {!collapsed && (
              <div className="px-2.5 pb-1.5 pt-3.5 text-[10.5px] font-bold uppercase tracking-wider text-fg-subtle">
                {grp.group}
              </div>
            )}
            {collapsed && <div className="my-2 border-t border-border" />}
            <div className="space-y-0.5">
              {grp.items.map((item) => (
                <SidebarLink key={item.to} item={item} collapsed={collapsed} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* ── User footer ────────────────────────────────────────────────────── */}
      <div className="px-2 py-3 border-t border-border">
        <div
          className={clsx(
            'flex items-center gap-2',
            collapsed ? 'justify-center' : 'px-2',
          )}
        >
          {/* Avatar */}
          <div
            className="h-7 w-7 rounded-full bg-accent text-accent-fg flex items-center justify-center text-xs font-medium shrink-0"
            aria-label={displayName}
          >
            {initial}
          </div>

          {!collapsed && (
            <>
              <span className="text-fg-muted text-sm truncate flex-1">{displayName}</span>
              <button
                onClick={logout}
                title="Sign out"
                aria-label="Sign out"
                className="text-fg-muted hover:text-fg transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>
    </aside>
  )
}
